import { useCallback, useEffect, useMemo, useState } from "react";
import {
  assignServiceRouteEvent,
  createServiceRoute,
  listServiceRoutes,
  reorderServiceRouteEvents,
} from "@/lib/service/client";
import type {
  ServiceEvent,
  ServiceRig,
  ServiceRouteDetail,
  ServiceTech,
} from "@/lib/service/types";

export function useServiceRouteComposer(
  events: ServiceEvent[],
  rigs: ServiceRig[],
  _techs: ServiceTech[],
  navTick: number,
  onAfterChange: () => void
) {
  const [routeDate, setRouteDate] = useState("");
  const [selectedRigId, setSelectedRigId] = useState("");
  const [selectedTechId, setSelectedTechId] = useState("");
  const [routes, setRoutes] = useState<ServiceRouteDetail[]>([]);
  const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);
  const [listBusy, setListBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [assignWarnings, setAssignWarnings] = useState<string[]>([]);
  const [dragEventId, setDragEventId] = useState<string | null>(null);

  const reloadRoutes = useCallback(async () => {
    if (!routeDate) {
      setRoutes([]);
      return;
    }
    setListBusy(true);
    setLocalError(null);
    const res = await listServiceRoutes({ route_date: routeDate, include_events: true });
    setListBusy(false);
    if (!res.ok) {
      setRoutes([]);
      setLocalError(res.error);
      return;
    }
    setRoutes(res.data.routes as ServiceRouteDetail[]);
  }, [routeDate]);

  useEffect(() => {
    void reloadRoutes();
  }, [reloadRoutes, navTick]);

  const unscheduledEvents = useMemo(
    () =>
      events.filter(
        (e) =>
          e.status !== "complete" &&
          e.status !== "cancelled" &&
          (!e.scheduled_date || e.scheduled_date === routeDate)
      ),
    [events, routeDate]
  );

  const selectedRoute = useMemo(
    () => routes.find((r) => r.id === selectedRouteId) ?? null,
    [routes, selectedRouteId]
  );

  const createRoute = useCallback(async () => {
    if (!routeDate || !selectedRigId) {
      setLocalError("Pick a route date and rig first.");
      return null;
    }
    setActionBusy(true);
    setLocalError(null);
    const rig = rigs.find((r) => r.id === selectedRigId);
    const res = await createServiceRoute({
      route_date: routeDate,
      rig_id: selectedRigId,
      tech_id: selectedTechId || rig?.standard_tech_id || null,
      label: rig ? `${rig.designation} — ${routeDate}` : routeDate,
    });
    setActionBusy(false);
    if (!res.ok) {
      setLocalError(res.error);
      return null;
    }
    await reloadRoutes();
    setSelectedRouteId(res.data.route.id);
    onAfterChange();
    return res.data.route;
  }, [routeDate, selectedRigId, selectedTechId, rigs, reloadRoutes, onAfterChange]);

  const assignEvent = useCallback(
    async (routeId: string, eventId: string) => {
      setActionBusy(true);
      setLocalError(null);
      setAssignWarnings([]);
      const res = await assignServiceRouteEvent({ route_id: routeId, event_id: eventId });
      setActionBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return false;
      }
      setAssignWarnings(res.data.warnings ?? []);
      await reloadRoutes();
      onAfterChange();
      return true;
    },
    [reloadRoutes, onAfterChange]
  );

  const reorderStops = useCallback(
    async (routeId: string, orderedEventIds: string[]) => {
      setActionBusy(true);
      setLocalError(null);
      const res = await reorderServiceRouteEvents({
        route_id: routeId,
        ordered_event_ids: orderedEventIds,
      });
      setActionBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return;
      }
      await reloadRoutes();
    },
    [reloadRoutes]
  );

  const moveStop = useCallback(
    (routeId: string, eventId: string, direction: -1 | 1) => {
      const route = routes.find((r) => r.id === routeId);
      if (!route) return;
      const ids = route.stops.map((s) => s.event.id);
      const idx = ids.indexOf(eventId);
      if (idx < 0) return;
      const target = idx + direction;
      if (target < 0 || target >= ids.length) return;
      const next = [...ids];
      const [removed] = next.splice(idx, 1);
      next.splice(target, 0, removed!);
      void reorderStops(routeId, next);
    },
    [routes, reorderStops]
  );

  const dropOnStop = useCallback(
    (routeId: string, targetEventId: string) => {
      if (!dragEventId || dragEventId === targetEventId) return;
      const route = routes.find((r) => r.id === routeId);
      if (!route) return;
      const ids = route.stops.map((s) => s.event.id);
      const from = ids.indexOf(dragEventId);
      const to = ids.indexOf(targetEventId);
      if (from < 0 || to < 0) return;
      const next = [...ids];
      const [removed] = next.splice(from, 1);
      next.splice(to, 0, removed!);
      setDragEventId(null);
      void reorderStops(routeId, next);
    },
    [dragEventId, routes, reorderStops]
  );

  return {
    routeDate,
    setRouteDate,
    selectedRigId,
    setSelectedRigId,
    selectedTechId,
    setSelectedTechId,
    routes,
    selectedRouteId,
    setSelectedRouteId,
    selectedRoute,
    unscheduledEvents,
    listBusy,
    actionBusy,
    localError,
    assignWarnings,
    dragEventId,
    setDragEventId,
    createRoute,
    assignEvent,
    moveStop,
    dropOnStop,
    reloadRoutes,
  };
}
